import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Network, ScanLine, Boxes, Gauge, Sparkles, Play, ShieldCheck } from "lucide-react";

const FEATURES = [
  { icon: ScanLine, title: "סריקת רשת חכמה", desc: "גילוי כל המכשירים עם IP, יצרן, שם וסוג" },
  { icon: Boxes, title: "מפה תלת-ממדית", desc: "נתב מרכזי וכל המכשירים סביבו, לפי רשתות" },
  { icon: Gauge, title: "מהירות וניטור", desc: "מדידת מהירות ואיחור עם גרף חי" },
];

export default function WelcomeOverlay({ open, onStart, onDemo }) {
  const [dontShow, setDontShow] = useState(true);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 z-50 flex items-center justify-center bg-[#F4F5F7]/70 p-4 backdrop-blur-md"
          data-testid="welcome-overlay"
        >
          <motion.div
            initial={{ scale: 0.94, opacity: 0, y: 14 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.94, opacity: 0, y: 14 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            dir="rtl"
            className="max-h-[95vh] w-[560px] max-w-full overflow-y-auto rounded-3xl border border-white/60 bg-white/95 shadow-2xl backdrop-blur-xl"
          >
            {/* branded header */}
            <div className="relative flex items-center gap-3 bg-[#0055FF] px-6 py-5 text-white">
              <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/15">
                <Network size={22} strokeWidth={2.4} />
              </span>
              <div>
                <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-widest text-white/70">
                  <Sparkles size={12} /> wase3.com
                </div>
                <h1 className="text-[19px] font-extrabold leading-tight">
                  מפת הרשת התלת-ממדית
                </h1>
              </div>
            </div>

            <div className="p-6">
              <p className="mb-5 text-[13.5px] leading-relaxed text-[#52525B]">
                ברוכים הבאים! כלי אינטראקטיבי לצפייה ברשת שלכם בתלת-ממד — גלו מי מחובר,
                באיזו כתובת IP, ובאיזה סוג מכשיר. כל הפעולות זמינות ישירות מהאתר.
              </p>

              <div className="mb-5 grid gap-2.5">
                {FEATURES.map((f) => {
                  const Icon = f.icon;
                  return (
                    <div key={f.title} className="flex items-center gap-3 rounded-xl border border-black/5 bg-white/70 p-3">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#0055FF]/10 text-[#0055FF]">
                        <Icon size={17} strokeWidth={2.2} />
                      </span>
                      <div>
                        <div className="text-[13px] font-bold text-[#0A0A0A]">{f.title}</div>
                        <div className="text-[11.5px] text-[#52525B]">{f.desc}</div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mb-5 flex items-start gap-2 rounded-xl bg-amber-50 p-3 text-[11.5px] leading-relaxed text-amber-800 ring-1 ring-amber-200">
                <ShieldCheck size={15} className="mt-0.5 shrink-0" />
                <span>
                  שימו לב: הגרסה שבאתר סורקת את רשת השרת, ולכן מציגה בעיקר <b>מצב הדגמה</b>.
                  לגילוי מכשירי הבית האמיתיים — הריצו את הגרסה המקומית (הורדה מכפתור "ייצוא").
                </span>
              </div>

              <div className="flex flex-col gap-3 sm:flex-row">
                <button
                  data-testid="welcome-demo"
                  onClick={() => onDemo(dontShow)}
                  className="flex flex-1 items-center justify-center gap-2 rounded-2xl bg-[#0055FF] px-5 py-3 text-[14px] font-bold text-white shadow-md transition-[transform,background-color] duration-200 hover:-translate-y-0.5 hover:bg-[#0047d6]"
                >
                  <Play size={16} />
                  צפו בהדגמה
                </button>
                <button
                  data-testid="welcome-start"
                  onClick={() => onStart(dontShow)}
                  className="flex flex-1 items-center justify-center gap-2 rounded-2xl border border-black/10 bg-white px-5 py-3 text-[14px] font-bold text-[#0A0A0A] transition-[transform,background-color] duration-200 hover:-translate-y-0.5 hover:bg-black/[0.03]"
                >
                  <ScanLine size={16} />
                  סרוק את הרשת
                </button>
              </div>

              <label className="mt-4 flex cursor-pointer items-center justify-center gap-2 text-[12px] text-[#52525B]">
                <input
                  data-testid="welcome-dontshow"
                  type="checkbox"
                  checked={dontShow}
                  onChange={(e) => setDontShow(e.target.checked)}
                  className="h-4 w-4 accent-[#0055FF]"
                />
                אל תציגו שוב
              </label>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
